// src/components/demo/DemoFlow.js
//
// State-machine orchestrator for the demo-day check-out app.
// Mounted by App.js when REACT_APP_FLAVOR=demo | demo_preview.
// SPEC: /docs/SPEC-2026-07-22-demo-checkout.md
//
// The one rule (SPEC §4): scan first, then the app decides. No react-router —
// this is a kiosk-style tool with no deep links; screens are a plain state
// machine, which also keeps the scan loop resilient offline.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  signInWithPopup, GoogleAuthProvider, OAuthProvider, onAuthStateChanged, signOut,
} from 'firebase/auth';
import { enableIndexedDbPersistence } from 'firebase/firestore';
import { auth, db } from '../../config/firebase';
import { parseSku } from '../../config/products';
import { WAIVER_VERSION, WAIVER_TEXT, computeWaiverHash } from '../../config/demoWaiver';
import {
  getStaffRole, watchPendingStaffRequests,
  getDefaultDayId, setDefaultDayId, clearDefaultDayId, getDemoDay, getSpot,
  isPastDay, formatDayRange,
  getFleetBoard, addBoardToFleet, getRegisteredOwner, clearMaintenance, watchFleet,
  emailDemoDayReport,
  findWaiverOnFile, createWaiver, createLead,
  checkOut, checkIn, voidSession, getSession, watchOpenSessions,
} from '../../services/demoService';

import { FScreen, FHeader, FHeadline, FBtn, FLabel } from '../foam/FoamPrimitives';
import DemoStaffGate from './DemoStaffGate';
import DemoApprovals from './DemoApprovals';
import DemoSpotSetup from './DemoSpotSetup';
import DemoScanHome from './DemoScanHome';
import DemoLeadForm from './DemoLeadForm';
import DemoBoardScreen from './DemoBoardScreen';
import DemoRiderForm from './DemoRiderForm';
import DemoPhotoCapture from './DemoPhotoCapture';
import DemoWaiverScreen from './DemoWaiverScreen';
import DemoConfirm from './DemoConfirm';
import DemoCheckIn from './DemoCheckIn';
import DemoFleetBoard from './DemoFleetBoard';

// Offline-first: queue Firestore writes while the beach signal drops
// (SPEC §4.5 / §10). Multi-tab conflicts and legacy browsers fail soft.
try { enableIndexedDbPersistence(db).catch(() => {}); } catch { /* no-op */ }

// Explicit dead-end screen. Every screen below has a data precondition
// (product, rider, boardSession…); when one was missing the render used to fall
// through every branch to scan home, so "Check in" read as a dead button
// (2026-08-01). Staff get told what happened and how to get out.
const DemoFlowError = ({ message, onBack }) => (
  <FScreen
    header={<FHeader right={<FLabel color="var(--foam-orange)">Something's off</FLabel>} />}
    footer={<FBtn primary onClick={onBack}>Back to scan</FBtn>}
  >
    <FHeadline size={44}>Can&apos;t show<br />that screen.</FHeadline>
    <div style={{ border: '1.5px solid var(--foam-orange)', background: '#FFF7F3', padding: 18 }}>
      <p className="font-sans text-[15px]" style={{ color: 'var(--foam-ink)', lineHeight: 1.5 }}>
        {message}
      </p>
    </div>
    <p className="font-sans text-[12px]" style={{ color: 'var(--foam-ink-2)' }}>
      Scan the board again. If it keeps happening, check the demo day shown at the top of the scan
      screen matches the other staff phone.
    </p>
  </FScreen>
);

const signInWithGoogle = async () => {
  const provider = new GoogleAuthProvider();
  return (await signInWithPopup(auth, provider)).user;
};

// Apple sign-in for staff without Google accounts (design handoff 2026-07-22
// addition; provider enabled project-wide 2026-06-05 — my-waka SPEC §2.1
// correction. demo.waka.surf must be added to Auth authorized domains).
const signInWithApple = async () => {
  const provider = new OAuthProvider('apple.com');
  provider.addScope('email');
  provider.addScope('name');
  return (await signInWithPopup(auth, provider)).user;
};

export default function DemoFlow() {
  // ── Auth + role
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);           // 'admin' | 'waka' | 'partner' | null
  const [staffDoc, setStaffDoc] = useState(null);
  const [roleLoaded, setRoleLoaded] = useState(false);

  // ── Demo day + spot + live data
  //
  // The DAY is the scope. Everything the app reads and writes is keyed by
  // eventId, and eventId IS the demoDays doc id ({spotId}_{date}) — we take it
  // from the selected day doc and never recompute it. Before 2026-08-01 the
  // date lived in memory and defaulted to today while only the SPOT was
  // persisted, so a phone that restored its sticky spot scoped itself to an
  // event nobody had created and ran a parallel fleet all day (handoff
  // 2026-08-02 §1). No day doc → no eventId → the picker, every time.
  const [demoDay, setDemoDay] = useState(null);
  const [dayRestored, setDayRestored] = useState(false);
  const [spot, setSpot] = useState(null);
  // Why the picker is showing, when it's showing because we took the day away.
  // A silent bounce to the picker reads as a glitch; staff need to know the
  // phone was on a finished event (round 2 §1).
  const [dayNotice, setDayNotice] = useState(null);
  const [fleet, setFleet] = useState([]);
  const [openSessions, setOpenSessions] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);

  // ── Screen machine
  const [screen, setScreen] = useState('home');     // home|approvals|spotSetup|board|rider|photo|waiver|confirm|checkin|fleet
  const [product, setProduct] = useState(null);
  const [fleetBoard, setFleetBoard] = useState(null);
  const [registeredOwner, setRegisteredOwner] = useState(null);
  const [boardSession, setBoardSession] = useState(null);
  const [rider, setRider] = useState(null);
  const [riderChecking, setRiderChecking] = useState(false);
  const [waiverOnFile, setWaiverOnFile] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [signing, setSigning] = useState(null);
  const [confirmDone, setConfirmDone] = useState(false);
  const [checkinDone, setCheckinDone] = useState(false);
  // Message for the explicit error screen (replaces silent fall-through).
  const [flowError, setFlowError] = useState(null);

  const eventId = demoDay?.id || null;
  const dayDateLabel = useMemo(() => formatDayRange(demoDay), [demoDay]);
  const waiverVersion = spot?.waiverVersion || WAIVER_VERSION;
  const canApprove = role === 'admin' || role === 'waka';

  // Auth → role
  useEffect(() => onAuthStateChanged(auth, async (u) => {
    setUser(u);
    setRoleLoaded(false);
    if (u) {
      const r = await getStaffRole(u);
      setRole(r.role);
      setStaffDoc(r.staffDoc);
    } else {
      setRole(null);
      setStaffDoc(null);
    }
    setRoleLoaded(true);
  }), []);

  // Role → restore the sticky demo DAY.
  //
  // No-orphan-event guard: we only adopt a stored day if its demoDays doc still
  // exists on the server. A missing doc (deleted, renamed, or a leftover key
  // from a previous event) drops the stored id and sends the user to the
  // picker. There is deliberately no path from "remembered something" to a
  // usable eventId that skips this check.
  useEffect(() => {
    let cancelled = false;
    setDayRestored(false);
    if (!role) {
      setDemoDay(null); setSpot(null); setDayRestored(true);
      return undefined;
    }
    const savedDayId = getDefaultDayId();
    if (!savedDayId) {
      setDemoDay(null); setSpot(null); setDayRestored(true);
      return undefined;
    }

    (async () => {
      try {
        const day = await getDemoDay(savedDayId);
        if (cancelled) return;
        if (!day) {
          // The day is gone — forget it rather than inventing a scope from it.
          clearDefaultDayId();
          setDemoDay(null); setSpot(null);
          return;
        }
        if (isPastDay(day)) {
          // Existing, but finished. Same treatment as a missing doc: a stale
          // phone must not resume writing into a closed event (round 2 §1).
          clearDefaultDayId();
          setDemoDay(null); setSpot(null);
          setDayNotice(
            `This phone was still on ${day.spotName || day.spotId} — ${formatDayRange(day) || day.date}, `
            + 'which has finished. Pick the current demo day to carry on.',
          );
          return;
        }
        const s = (await getSpot(day.spotId))
          || { id: day.spotId, name: day.spotName, overdueMinutes: 60, photoCapture: 'off' };
        if (cancelled) return;
        setDemoDay(day);
        setSpot(s);
      } catch {
        // Read failed (offline on first load, rules, …). Leave both null so the
        // picker is shown; guessing a scope here is exactly the old bug.
      } finally {
        if (!cancelled) setDayRestored(true);
      }
    })();
    return () => { cancelled = true; };
  }, [role]);

  // Live fleet + open sessions for the day
  useEffect(() => {
    if (!role || !eventId) return undefined;
    const un1 = watchFleet(eventId, setFleet);
    const un2 = watchOpenSessions(eventId, setOpenSessions);
    return () => { un1(); un2(); };
  }, [role, eventId]);

  // Pending approvals badge (WAKA staff/admin only)
  useEffect(() => {
    if (!canApprove) { setPendingRequests([]); return undefined; }
    return watchPendingStaffRequests(setPendingRequests);
  }, [canApprove]);

  const resetLoop = useCallback(() => {
    setScreen('home');
    setProduct(null); setFleetBoard(null); setBoardSession(null);
    setRider(null); setWaiverOnFile(null); setPhoto(null); setSigning(null);
    setConfirmDone(false); setCheckinDone(false); setFlowError(null);
  }, []);

  const failTo = useCallback((message) => {
    setFlowError(message);
    setScreen('error');
  }, []);

  // Re-validate the day at the point of use, not only at mount. A phone that
  // stays open across midnight is scoped to yesterday from the first tap after
  // 00:00 and nothing would otherwise notice (round 2 §1). Returns true if the
  // day was expired out from under the caller.
  const expireDayIfPast = useCallback(() => {
    if (!demoDay || !isPastDay(demoDay)) return false;
    clearDefaultDayId();
    setDayNotice(
      `${demoDay.spotName || demoDay.spotId} — ${formatDayRange(demoDay) || demoDay.date} `
      + 'has finished. Pick the current demo day to carry on.',
    );
    setDemoDay(null);
    setSpot(null);
    resetLoop();
    return true;
  }, [demoDay, resetLoop]);

  // Backgrounded phone coming back is the common case for crossing midnight.
  // NOTE: this re-checks the DAY only. Re-subscribing the Firestore listeners
  // on visibilitychange is round 1 §6 out-of-scope and is deliberately not done
  // here.
  useEffect(() => {
    if (!demoDay) return undefined;
    const onVisibility = () => {
      if (document.visibilityState === 'visible') expireDayIfPast();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [demoDay, expireDayIfPast]);

  // ── Scan → board card (the branch point)
  const openBoard = useCallback(async (sku) => {
    const p = parseSku(sku);
    if (!p) { window.alert('Unknown WAKA serial.'); return; }
    // Refuse to open a board on a finished day — routes to the picker with a
    // notice naming the day, rather than bouncing silently (round 2 §1).
    if (expireDayIfPast()) return;
    if (!eventId) { failTo('No demo day is selected. Pick the day first, then scan.'); return; }
    setProduct(p);
    // These reads decide out-vs-in. A throw used to leave staff on the scan
    // screen with no feedback at all (2026-08-02 handoff §4.3).
    try {
      const fb = await getFleetBoard(eventId, p.sku);
      setFleetBoard(fb);
      // Personal-board guard: if not in fleet and the serial is registered,
      // the board card demands an explicit "add as loaner" confirmation.
      setRegisteredOwner(fb ? null : await getRegisteredOwner(p.sku));
      if (fb?.status === 'out' && fb.currentSessionId) {
        setBoardSession(await getSession(fb.currentSessionId));
      } else {
        setBoardSession(null);
      }
      setScreen('board');
    } catch (e) {
      failTo(`Couldn't load that board — ${e?.message || 'the read failed'}. Check the signal and scan again.`);
    }
  }, [eventId, failTo, expireDayIfPast]);

  // ── Rider continue → repeat-rider lookup → waiver (rider keeps the device).
  // Rider-types-own flow (Nigel 2026-07-22): the rider holds the device from
  // the details form through signing; the optional photo step comes AFTER
  // signing, once the device is back in staff hands (before confirm).
  const onRiderContinue = useCallback(async (r) => {
    setRiderChecking(true);
    try {
      const existing = await findWaiverOnFile(eventId, r.email, waiverVersion);
      setRider(r);
      setWaiverOnFile(existing);
      setScreen(existing ? 'confirm' : 'waiver');             // <15s path skips waiver AND photo
    } finally {
      setRiderChecking(false);
    }
  }, [eventId, waiverVersion]);

  // ── Waiver signed → optional staff photo step → confirm
  const onWaiverSigned = useCallback((s) => {
    setSigning(s);
    const mode = spot?.photoCapture || 'off';
    setScreen(mode === 'off' ? 'confirm' : 'photo');
  }, [spot]);

  // ── Check-out commit
  const onConfirmCheckOut = useCallback(async () => {
    let waiverId = waiverOnFile?.id;
    let consent = waiverOnFile ? waiverOnFile.emailConsent === true : signing?.emailConsent === true;
    if (!waiverId) {
      const created = await createWaiver(eventId, spot, rider, {
        ...signing,
        photoDataUrl: photo?.photoDataUrl || null,
        photoType: photo?.photoType || null,
        photoDeclined: photo?.photoDeclined === true,
        waiverVersion,
        waiverTextHash: await computeWaiverHash(WAIVER_TEXT),
      }, user);
      waiverId = created.id;
    }
    await checkOut(eventId, product, rider, waiverId, consent, user);
    setConfirmDone(true);
  }, [eventId, spot, rider, signing, photo, waiverOnFile, waiverVersion, product, user]);

  // ── Render
  if (!roleLoaded || (role && !dayRestored)) {
    // Auth restore / role fetch / day restore in flight — blank paper beats a
    // sign-in flash, and beats flashing the day picker at someone whose day is
    // about to come back.
    return <div style={{ minHeight: '100dvh', background: 'var(--foam-paper)' }} />;
  }

  if (!user || !role) {
    return (
      <DemoStaffGate
        user={roleLoaded ? user : null}
        role={role}
        staffDoc={staffDoc}
        onSignInGoogle={signInWithGoogle}
        onSignInApple={signInWithApple}
        onSignOut={() => signOut(auth)}
      />
    );
  }

  // No day (or no spot for it) → the picker. This is the only way to acquire an
  // eventId; there is no fallback that synthesises one.
  if (!demoDay || !spot || screen === 'spotSetup') {
    return (
      <DemoSpotSetup
        user={user}
        role={role}
        notice={dayNotice}
        onDismissNotice={() => setDayNotice(null)}
        onDaySelected={async (day, spotDoc) => {
          // day: {id, spotId, spotName, date} from demoDays; spotDoc provided
          // when the admin just created it (skips a read).
          const s = spotDoc || (await getSpot(day.spotId)) ||
            { id: day.spotId, name: day.spotName, overdueMinutes: 60, photoCapture: 'off' };
          setSpot(s);
          setDemoDay(day);
          setDayNotice(null);
          // Persist the DAY, not the spot: the day id is the eventId, so this
          // is the whole scope and it survives a reload without any date guess.
          setDefaultDayId(day.id);
          resetLoop();
        }}
      />
    );
  }

  if (screen === 'approvals') {
    return <DemoApprovals user={user} requests={pendingRequests} onBack={resetLoop} />;
  }

  if (screen === 'lead') {
    return (
      <DemoLeadForm
        user={user}
        eventId={eventId}
        onSubmit={(lead) => createLead(eventId, spot, lead, user)}
        onBack={resetLoop}
      />
    );
  }

  if (screen === 'fleet') {
    return (
      <DemoFleetBoard
        spot={spot}
        dateLabel={demoDay.date}
        fleet={fleet}
        openSessions={openSessions}
        canReport={canApprove}
        onEmailReport={() => emailDemoDayReport(eventId)}
        onPick={openBoard}
        onBack={resetLoop}
      />
    );
  }

  if (screen === 'board' && product) {
    return (
      <DemoBoardScreen
        product={product}
        fleetBoard={fleetBoard}
        openSession={boardSession}
        onCheckOut={() => setScreen('rider')}
        onCheckIn={() => setScreen('checkin')}
        registeredOwner={registeredOwner}
        onAddToFleet={async () => setFleetBoard(await addBoardToFleet(eventId, product, user, {
          loaner: !!registeredOwner,
          ownerName: registeredOwner?.ownerName || null,
        }))}
        onClearMaintenance={async () => {
          await clearMaintenance(eventId, product.sku);
          setFleetBoard(await getFleetBoard(eventId, product.sku));
        }}
        onBack={resetLoop}
      />
    );
  }

  if (screen === 'rider' && product) {
    return (
      <DemoRiderForm
        product={product}
        initial={rider}
        checking={riderChecking}
        onContinue={onRiderContinue}
        onBack={() => setScreen('board')}
      />
    );
  }

  if (screen === 'photo' && product) {
    return (
      <DemoPhotoCapture
        mode={spot.photoCapture === 'id' ? 'id' : 'face'}
        onDone={(p) => { setPhoto(p); setScreen('confirm'); }}
      />
    );
  }

  if (screen === 'waiver' && product && rider) {
    return (
      <DemoWaiverScreen
        rider={rider}
        onSigned={onWaiverSigned}
        onBack={() => setScreen('rider')}
      />
    );
  }

  if (screen === 'confirm' && product && rider) {
    return (
      <DemoConfirm
        product={product}
        rider={rider}
        waiverOnFile={!!waiverOnFile}
        emailConsent={waiverOnFile ? waiverOnFile.emailConsent === true : signing?.emailConsent === true}
        onConfirm={onConfirmCheckOut}
        onBack={confirmDone ? resetLoop : () => setScreen(waiverOnFile ? 'rider' : 'waiver')}
        done={confirmDone}
      />
    );
  }

  if (screen === 'checkin' && product && boardSession) {
    return (
      <DemoCheckIn
        product={product}
        session={boardSession}
        onConfirm={async (opts) => { await checkIn(boardSession, opts, user); setCheckinDone(true); }}
        onVoid={async (reason) => { await voidSession(boardSession, reason, user); resetLoop(); }}
        onBack={checkinDone ? resetLoop : () => setScreen('board')}
        done={checkinDone}
      />
    );
  }

  // A screen was requested but its data precondition above did not hold (or
  // openBoard failed). Say so — do NOT fall through to scan home, which is what
  // made "Check in" look like a dead button on 2026-08-01.
  if (screen !== 'home') {
    return (
      <DemoFlowError
        message={flowError
          || "That screen lost the board or rider it was showing — most likely the session was closed "
             + 'on another phone while you were on it.'}
        onBack={resetLoop}
      />
    );
  }

  // Default: scan home
  const overdueAfter = spot.overdueMinutes || 60;
  const overdueCount = openSessions.filter((s) => {
    const t = s.checkedOutAt?.toDate ? s.checkedOutAt.toDate() : null;
    return t && (Date.now() - t.getTime()) / 60000 >= overdueAfter;
  }).length;

  return (
    <DemoScanHome
      spot={spot}
      dayDateLabel={dayDateLabel}
      outCount={openSessions.length}
      overdueCount={overdueCount}
      pendingApprovals={pendingRequests.length}
      canApprove={canApprove}
      onSku={openBoard}
      onFleet={() => setScreen('fleet')}
      onChangeSpot={() => setScreen('spotSetup')}
      onApprovals={() => setScreen('approvals')}
      onLead={() => setScreen('lead')}
      onSignOut={() => signOut(auth)}
    />
  );
}
